import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Text from "@/components/ui/text";
import Spinner from "@/components/ui/spinner";
import IconContainer from "@/components/ui/icon-container";
import { IconButton } from "@/components/ui/icon-button";
import EntityLogoIconContainer from "@/components/ui/entity-logo-icon-container";
import { Add, ChevronRight, Close, Minus } from "@dreamhub/designsystem/icons";
import { Button } from "@/components/ui/button";
import OptionsDropdownMenu, {
  DetailedViewOptions
} from "@/components/controls/OptionsDropdownMenu/OptionsDropdownMenu";
import { Download } from "@dreamhub/designsystem/icons";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { TrackedProposalStatus } from "../TrackedProposalStatus";
import { Proposal } from "../types";
import { PageOverlay, PdfDocument } from "./PdfDocument";
import { dealPath } from "./paths";
import { visiblePage } from "./visiblePage";
import { MAX_ZOOM, MIN_ZOOM, ZOOM_STEP, nextZoom } from "./zoom";

// Shared by the editor and the read-only view.
export const DOCUMENT_PAGE_WIDTH = 720;
// The app shell insets its content by mt-14 + mb-6 (80px) but --app-content-h
// only subtracts the 64px nav, so a page that fills it overflows the window by
// the 16px remainder. Subtract it so the panes are the only scrollers.
export const PAGE_HEIGHT_CLASS =
  "flex flex-col h-full max-h-[calc(var(--app-content-h)-16px)]";
export const PANE_ROW = "flex flex-1 min-h-0 gap-2 px-2 pb-2";
export const CARD =
  "rounded-2xl border border-border-widget-card bg-surface-widget-card";
export const DOCUMENT_PANE = `${CARD} bg-[radial-gradient(var(--white-8)_1px,transparent_1px)] bg-[length:16px_16px]`;

interface HeaderProps {
  dealId: string;
  dealName?: string;
  // The last crumb: the proposal's name, or the field renaming it.
  title?: React.ReactNode;
  actions?: React.ReactNode;
}

export const EditorHeader: React.FC<HeaderProps> = ({
  dealId,
  dealName,
  title,
  actions
}) => {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-1 min-w-0 px-4 py-4">
      <EntityLogoIconContainer entityType={EntityType.DEAL} />
      <Link to="/deals" className="shrink-0">
        <Text variant="lgSubtitleH3" className="p-1">
          {t("pages.deals")}
        </Text>
      </Link>
      <IconContainer color="secondary" size="5" className="shrink-0">
        <ChevronRight />
      </IconContainer>
      <Link to={dealPath(dealId)} className="min-w-0">
        <Text variant="lgSubtitleH3" className="p-1 truncate">
          {dealName ?? ""}
        </Text>
      </Link>
      <IconContainer color="secondary" size="5" className="shrink-0">
        <ChevronRight />
      </IconContainer>
      {title}
      {actions ? (
        <div className="flex items-center gap-2 ml-auto shrink-0">
          {actions}
        </div>
      ) : null}
    </div>
  );
};

const TOOLBAR_CHIP =
  "flex items-center gap-1 h-7 px-2 rounded-lg border border-border-widget-card bg-surface-widget-card";

interface ToolbarProps {
  proposal: Proposal;
  zoom: number;
  onZoom: (step: number) => void;
  page: number;
  pageCount?: number;
  refreshing: boolean;
  // The editor's close; the read-only view has none.
  onClose?: () => void;
  trackingPaused: boolean;
}

const DocumentToolbar: React.FC<ToolbarProps> = ({
  proposal,
  zoom,
  onZoom,
  page,
  pageCount,
  refreshing,
  onClose,
  trackingPaused
}) => {
  const { t } = useTranslation();
  return (
    <div className="absolute top-2 inset-x-2 z-10 flex items-center justify-between gap-2">
      <div className="flex items-center gap-2">
        <TrackedProposalStatus
          proposal={proposal}
          trackingPaused={trackingPaused}
        />
        {refreshing ? (
          <div
            className="flex items-center gap-1"
            role="status"
            data-test="proposal-preview-refreshing"
          >
            <Spinner size="sm" />
            <Text variant="smBodySecondary">
              {t("contracts.proposals.editor.refreshing")}
            </Text>
          </div>
        ) : null}
      </div>
      <div className="flex items-center gap-2">
        <div className={TOOLBAR_CHIP}>
          <Text variant="smBodySecondary">
            {t("contracts.proposals.editor.zoom")}
          </Text>
          <Text variant="smBody" data-test="proposal-zoom-level">
            {`${zoom}%`}
          </Text>
          <IconButton
            variant="iconNoBg"
            size="xs"
            iconColor="secondary"
            onClick={() => onZoom(-ZOOM_STEP)}
            disabled={zoom <= MIN_ZOOM}
            aria-label={t("contracts.proposals.editor.zoomOut")}
          >
            <Minus />
          </IconButton>
          <IconButton
            variant="iconNoBg"
            size="xs"
            iconColor="secondary"
            onClick={() => onZoom(ZOOM_STEP)}
            disabled={zoom >= MAX_ZOOM}
            aria-label={t("contracts.proposals.editor.zoomIn")}
          >
            <Add />
          </IconButton>
        </div>
        <div className={TOOLBAR_CHIP}>
          <Text variant="smBodySecondary">
            {t("contracts.proposals.editor.page")}
          </Text>
          <Text variant="smBody" data-test="proposal-page-count">
            {pageCount ? `${page}/${pageCount}` : "–"}
          </Text>
        </div>
        {onClose ? (
          <IconButton
            variant="iconNoBg"
            size="sm"
            iconColor="secondary"
            onClick={onClose}
            aria-label={t("contracts.proposals.editor.close")}
            data-test="proposal-editor-close"
          >
            <Close />
          </IconButton>
        ) : null}
      </div>
    </div>
  );
};

interface PaneProps {
  proposal: Proposal;
  // None while the file to show is still being read.
  url: string | undefined;
  // The file to show couldn't be read.
  failed?: boolean;
  refreshing: boolean;
  onClose?: () => void;
  renderOverlay?: PageOverlay;
  // Pinned over the pane's foot, such as the editor's save bar; the pages
  // scroll clear of it.
  footer?: React.ReactNode;
  // Scrolls to the first spot this slot takes; a new object scrolls again.
  locate?: { slotId: string };
  // Draws the pages itself, at this width, in place of the file at `url`.
  renderPages?: (
    width: number,
    onPageCount: (pageCount: number) => void
  ) => React.ReactNode;
  // The status shown is the last one known: the e-sign connection is down.
  trackingPaused?: boolean;
}

// How far below the pane's top a located spot lands, as a share of its height.
const LOCATE_OFFSET = 0.3;

// A field can be drawn more than once: a paragraph split across pages shows each
// page's lines through a window over the whole paragraph. The spot is the copy its
// window shows; outside any window, the first one.
const shownAnchor = (scroller: HTMLElement, slotId: string) => {
  const anchors = Array.from(
    scroller.querySelectorAll<HTMLElement>(
      `[data-slot-anchor="${CSS.escape(slotId)}"]`
    )
  );
  const shown = anchors.find((anchor) => {
    const window = anchor.closest("[data-lines-window]");
    if (!window) return true;
    const spot = anchor.getBoundingClientRect();
    const bounds = window.getBoundingClientRect();
    return spot.bottom > bounds.top && spot.top < bounds.bottom;
  });
  return shown ?? anchors[0];
};

// The document with its toolbar; zoom and the page in view are its own.
export const DocumentPane: React.FC<PaneProps> = ({
  proposal,
  url,
  refreshing,
  onClose,
  renderOverlay,
  footer,
  locate,
  renderPages,
  trackingPaused = false,
  failed = false
}) => {
  const { t } = useTranslation();
  const [zoom, setZoom] = useState(100);
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState<number>();
  const scrollerRef = useRef<HTMLDivElement>(null);

  // Zooming moves the pages without a scroll event, so re-measure.
  useEffect(() => {
    if (scrollerRef.current) setPage(visiblePage(scrollerRef.current));
  }, [zoom]);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller || !locate) return;
    const anchor = shownAnchor(scroller, locate.slotId);
    if (!anchor) return;
    const spot = anchor.getBoundingClientRect();
    const pane = scroller.getBoundingClientRect();
    scroller.scrollTo({
      top:
        scroller.scrollTop +
        spot.top -
        pane.top -
        scroller.clientHeight * LOCATE_OFFSET,
      left:
        scroller.scrollLeft +
        spot.left -
        pane.left -
        (scroller.clientWidth - spot.width) / 2,
      behavior: "smooth"
    });
  }, [locate]);

  return (
    <div
      className={`relative flex-1 min-w-0 overflow-hidden ${DOCUMENT_PANE}`}
      aria-label={t("contracts.proposals.editor.documentLabel")}
      data-test="proposal-document-pane"
    >
      <DocumentToolbar
        proposal={proposal}
        zoom={zoom}
        onZoom={(step) => setZoom((current) => nextZoom(current, step))}
        page={page}
        pageCount={pageCount}
        refreshing={refreshing}
        onClose={onClose}
        trackingPaused={trackingPaused}
      />
      <div
        ref={scrollerRef}
        className={`h-full overflow-auto px-2 pt-12 ${footer ? "pb-20" : "pb-4"}`}
        onScroll={(event) => setPage(visiblePage(event.currentTarget))}
        data-test="proposal-document-scroller"
      >
        <div className="w-fit min-w-full">
          {failed ? (
            <div className="flex justify-center py-10">
              <Text variant="bodySecondary" data-test="proposal-document-error">
                {t("contracts.proposals.editor.documentError")}
              </Text>
            </div>
          ) : renderPages ? (
            renderPages((DOCUMENT_PAGE_WIDTH * zoom) / 100, setPageCount)
          ) : url ? (
            <PdfDocument
              url={url}
              width={(DOCUMENT_PAGE_WIDTH * zoom) / 100}
              onPageCount={setPageCount}
              renderOverlay={renderOverlay}
            />
          ) : (
            <div
              className="flex justify-center py-10"
              data-test="proposal-document-loading"
            >
              <Spinner size="md" />
            </div>
          )}
        </div>
      </div>
      {footer}
    </div>
  );
};

// The proposal couldn't be opened; the rep can ask again.
export const OpenError: React.FC<{ onRetry?: () => void }> = ({ onRetry }) => {
  const { t } = useTranslation();
  return (
    <div
      className="flex flex-1 flex-col items-center justify-center gap-3"
      role="alert"
      data-test="proposal-open-error"
    >
      <Text variant="bodySecondary">
        {t("contracts.proposals.editor.openError")}
      </Text>
      <Button variant="secondary" onClick={onRetry}>
        {t("contracts.proposals.retry")}
      </Button>
    </div>
  );
};

// The proposal was deleted, so there is nothing to open.
export const DeletedNotice: React.FC = () => {
  const { t } = useTranslation();
  return (
    <div
      className="flex flex-1 flex-col items-center justify-center"
      role="status"
      data-test="proposal-deleted"
    >
      <Text variant="bodySecondary">
        {t("contracts.proposals.editor.deleted")}
      </Text>
    </div>
  );
};

export const ProposalActionsMenu: React.FC<{
  options: DetailedViewOptions;
}> = ({ options }) => {
  const { t } = useTranslation();
  return (
    <OptionsDropdownMenu
      options={options}
      tPrefix="contracts.proposals.actions"
      iconSize="lg"
      tooltipText={t("contracts.proposals.editor.actions")}
      triggerDataTest="proposal-actions"
      // Rename moves focus into the title field; handing it back to the
      // trigger on close would blur the field at once.
      disableCloseAutoFocus
    />
  );
};

export const DownloadButton: React.FC<{
  onClick: () => void;
  disabled: boolean;
}> = ({ onClick, disabled }) => {
  const { t } = useTranslation();
  return (
    <Button
      variant="secondary"
      onClick={onClick}
      disabled={disabled}
      data-test="proposal-download"
    >
      <IconContainer as="span" size="sm" color="secondary" className="shrink-0">
        <Download />
      </IconContainer>
      {t("contracts.proposals.editor.download")}
    </Button>
  );
};
